import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  ADMIN › DOKUMENTUMOK › ARCHÍV ÁTADÁS (backend/archiv-atadas.js)
 *
 *  Nkt. 57. § (9a)–(9b): a digitális bizonyítványt a jogviszony megszűnésétől 80 évig elérhetővé
 *  kell tenni, jogutód nélküli megszűnéskor archív hozzáféréssel. Itt:
 *    - az intézmény összes érvényes digitális bizonyítványa egy átadási csomagban (ZIP: PDF-ek +
 *      jegyzék CSV/JSON + SHA-256), a korábbi átadások naplójával;
 *    - jogutódi / fenntartói / levéltári olvasási jog egy külső belépésnek (név vagy e-mail);
 *    - ha a belépőnek MÁS intézmények archívumához van ilyen joga, azok csomagja is innen tölthető.
 */
@Component({
  selector: 'app-archiv-atadas',
  templateUrl: './archiv-atadas.component.html',
  styleUrls: ['./archiv-atadas.component.scss'],
})
export class ArchivAtadasComponent implements OnInit {
  intezmeny = '';
  betolt = false;
  /** A saját intézmény jegyzéke (null: nincs jog / még nem jött meg). */
  jegyzek: any = null;
  jegyzekHiba = '';
  tanevek: { tanev: string; db: number }[] = [];
  csomagKeszul: Record<string, boolean> = {};

  jogosultak: any[] = [];
  jogosultHiba = '';
  uj = { felhasznalo: '', megnevezes: '', szerep: 'jogutod', jogalap: '' };
  mentes = false;

  /** Más intézmények, amelyek archívumához a belépőnek jogutódi joga van. */
  idegen: any[] = [];

  readonly szerepek = [
    { ertek: 'jogutod', nev: 'Jogutód intézmény' },
    { ertek: 'fenntarto', nev: 'Fenntartó' },
    { ertek: 'leveltar', nev: 'Levéltár' },
  ];

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si: any = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    this.frissit();
  }

  szerepNev(s: string): string {
    return this.szerepek.find((x) => x.ertek === s)?.nev || s || '';
  }

  async frissit() {
    if (!this.intezmeny) return;
    this.betolt = true;
    this.jegyzekHiba = '';
    this.jogosultHiba = '';
    try {
      this.jegyzek = await firstValueFrom(this.http.get<any>(`/api/archiv-atadas/${this.intezmeny}/jegyzek`));
      this.tanevek = Object.entries(this.jegyzek?.tanevenkent || {})
        .map(([tanev, db]) => ({ tanev, db: Number(db) }))
        .sort((a, b) => b.tanev.localeCompare(a.tanev));
    } catch (e: any) {
      this.jegyzek = null;
      this.jegyzekHiba = e?.error?.error || 'A jegyzék nem tölthető be.';
    }
    try {
      this.jogosultak = await firstValueFrom(this.http.get<any[]>(`/api/archiv-atadas/${this.intezmeny}/jogosultak`)) || [];
    } catch (e: any) {
      this.jogosultak = [];
      this.jogosultHiba = e?.error?.error || 'A hozzáférések nem tölthetők be.';
    }
    try {
      const lista = await firstValueFrom(this.http.get<any[]>('/api/archiv-atadas/sajat')) || [];
      this.idegen = lista.filter((x) => String(x.intezmeny) !== this.intezmeny);
    } catch {
      this.idegen = [];
    }
    this.betolt = false;
  }

  /** Az átadási csomag letöltése (tokennel, blobként – a DataService.megnyitFajl intézi). */
  async csomag(intezmeny: string) {
    if (!intezmeny || this.csomagKeszul[intezmeny]) return;
    this.csomagKeszul[intezmeny] = true;
    try {
      await this.data.megnyitFajl(`/api/archiv-atadas/${intezmeny}/csomag`, `archiv-atadas-${intezmeny.replace(/\//g, '-')}.zip`);
    } finally {
      this.csomagKeszul[intezmeny] = false;
      if (intezmeny === this.intezmeny) this.frissit();
    }
  }

  async jogotAd() {
    const felhasznalo = this.uj.felhasznalo.trim();
    if (!felhasznalo) { this.data.sendToast('Add meg az átvevő belépési nevét vagy e-mail címét.', 'warning'); return; }
    this.mentes = true;
    try {
      await firstValueFrom(this.http.post(`/api/archiv-atadas/${this.intezmeny}/jogosultak`, { ...this.uj, felhasznalo }));
      this.data.sendToast('Az archív hozzáférés rögzítve.', 'success');
      this.uj = { felhasznalo: '', megnevezes: '', szerep: 'jogutod', jogalap: '' };
      await this.frissit();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült rögzíteni.', 'error');
    } finally {
      this.mentes = false;
    }
  }

  async visszavon(j: any) {
    if (!j?.id || j.visszavonva) return;
    if (!window.confirm(`Visszavonod ${j.megnevezes || j.felhasznalo} archív hozzáférését?`)) return;
    try {
      await firstValueFrom(this.http.delete(`/api/archiv-atadas/${this.intezmeny}/jogosultak/${j.id}`));
      this.data.sendToast('A hozzáférés visszavonva.', 'success');
      await this.frissit();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült visszavonni.', 'error');
    }
  }
}
